import React, { useState, useEffect } from "react";
import logo from "../../../assets/Aara_logo.png";
import PhoneInput from "react-phone-input-2";

const TestPrepStudentForm = ({
  formData,
  setFormData,
  handleSubmit,
  isSubmitted,
}) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validate the form
  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.phone) newErrors.phone = "Phone is required";
    else if (formData.phone.length < 6)
      newErrors.phone = "Enter a valid phone number";
    if (!formData.reference_source)
      newErrors.reference_source = "Reference source is required";
    if (
      formData.reference_source === "Reference of Friend/Relative" &&
      !formData.reference_name_id
    )
      newErrors.reference_name_id = "Referral name is required";
    if (
      formData.reference_source === "Others" &&
      !formData.other_reference_source
    )
      newErrors.other_reference_source = "Please specify reference";
    if (
      !formData.name_test_preparation_required ||
      formData.name_test_preparation_required.length === 0
    )
      newErrors.name_test_preparation_required = "Select at least one test";
    if (
      formData.name_test_preparation_required &&
      formData.name_test_preparation_required.includes("Others") &&
      !formData.other_exams
    )
      newErrors.other_exams = "Please specify other exams";
    if (!formData.exam_date) newErrors.exam_date = "Exam date is required";
    if (!formData.preferred_mode_of_teaching)
      newErrors.preferred_mode_of_teaching = "Preferred mode is required";
    if (!formData.country || formData.country.length === 0)
      newErrors.country = "Select at least one country";
    if (
      formData.country &&
      formData.country.includes("Others") &&
      !formData.other_country
    )
      newErrors.other_country = "Please specify other countries";
    return newErrors;
  };

  // Handle field blur for validation feedback
  // const handleBlur = (e) => {
  //   const { name } = e.target;
  //   setTouched({ ...touched, [name]: true });

  //   // Validate the specific field and update errors
  //   const validationErrors = validate();
  //   if (validationErrors[name]) {
  //     setErrors((prev) => ({ ...prev, [name]: validationErrors[name] }));
  //   } else {
  //     setErrors((prev) => {
  //       const newErrors = { ...prev };
  //       delete newErrors[name];
  //       return newErrors;
  //     });
  //   }
  // };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched(
        Object.keys(validationErrors).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );
    } else {
      setErrors({});
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "reference_source") {
        // Handle radio-like behavior for reference_source
        setFormData((prevState) => ({
          ...prevState,
          [name]: checked ? value : "",
        }));
      } else {
        // Handle test preparation checkboxes
        setFormData((prevState) => {
          const updatedTestPrep = checked
            ? [...(prevState.name_test_preparation_required || []), value]
            : (prevState.name_test_preparation_required || []).filter(
                (exam) => exam !== value
              );
          return {
            ...prevState,
            name_test_preparation_required: updatedTestPrep,
          };
        });
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    // Clear error for this field when value changes
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle phone input change (special case)
  const handlePhoneChange = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      phone: value,
    }));

    // Clear error when value changes
    if (errors.phone) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
    }
  };

  const handleReferenceChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: value,
        // Clear related fields when changing reference source
        reference_name_id:
          value !== "Reference of Friend/Relative"
            ? ""
            : prevState.reference_name_id,
        other_reference_source:
          value !== "Others" ? "" : prevState.other_reference_source,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: "", // Uncheck and clear the reference when the checkbox is unchecked
      }));
    }
  };

  const handleChangeCountry = (e) => {
    const { value, type, checked } = e.target;

    setFormData((prevState) => {
      let selectedCountries = Array.isArray(prevState.country)
        ? [...prevState.country]
        : [];

      if (type === "checkbox") {
        if (checked) {
          // Add country if not already in the array
          if (!selectedCountries.includes(value)) {
            selectedCountries.push(value);
          }
        } else {
          // Remove country if unchecked
          selectedCountries = selectedCountries.filter(
            (country) => country !== value
          );

          // Clear other_country if "Others" is unchecked
          if (value === "Others") {
            return {
              ...prevState,
              country: selectedCountries,
              other_country: "",
            };
          }
        }

        return {
          ...prevState,
          country: selectedCountries,
        };
      } else {
        // Handle "Others" input text separately
        return { ...prevState, other_country: value };
      }
    });
  };

  // Display error message for a field
  const getErrorMessage = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? (
      <p className="text-red-500 text-xs mt-1">{errors[fieldName]}</p>
    ) : null;
  };

  // Return success screen if submitted
  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
        <img src={logo} alt="Aara Consultancy Logo" className="h-24 mb-6" />
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
          Thank You for Submitting!
        </h1>
        <p className="text-lg text-gray-600 mb-6 text-center">
          We have received your information and will get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Logo Section */}
      <div className="flex justify-center mb-6">
        <img src={logo} alt="Aara Consultancy Logo" className="h-16 md:h-20" />
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-gray-100 shadow-lg p-4 md:p-6 rounded-lg max-w-4xl mx-auto"
      >
        <h2 className="text-xl md:text-2xl font-semibold text-center mb-4">
          Test Prep Student Information Form
        </h2>
        <p className="text-sm text-gray-500 mb-4 text-center">
          Fields marked with * are mandatory and cannot be left blank!
        </p>

        <div className="space-y-4">
          {/* Name */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <label
              htmlFor="name"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Name *
            </label>
            <div className="w-full md:w-1/2">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`border rounded-lg p-2 w-full ${
                  touched.name && errors.name
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {getErrorMessage("name")}
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <label
              htmlFor="email"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Email *
            </label>
            <div className="w-full md:w-1/2">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`border rounded-lg p-2 w-full ${
                  touched.email && errors.email
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {getErrorMessage("email")}
            </div>
          </div>

          {/* Mobile No */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <label
              htmlFor="phone"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Mobile No *
            </label>
            <div className="w-full md:w-1/2">
              <PhoneInput
                country={"in"}
                value={formData.phone || ""}
                onChange={handlePhoneChange}
                onBlur={() => {
                  setTouched({ ...touched, phone: true });
                }}
                inputProps={{
                  name: "phone",
                  id: "phone",
                }}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "6px",
                  borderColor:
                    touched.phone && errors.phone ? "#f56565" : "#ccc",
                  paddingLeft: "50px",
                }}
                containerStyle={{
                  width: "100%",
                }}
                dropdownStyle={{ width: "260px" }}
              />
              {getErrorMessage("phone")}
            </div>
          </div>

          {/* Reference */}
          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="reference_source"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              How did you get your reference? *
            </label>
            <div className="w-full md:w-1/2">
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-lg p-2 bg-white ${
                  touched.reference_source && errors.reference_source
                    ? "border-red-500"
                    : ""
                }`}
              >
                {[
                  "Reference of Friend/Relative",
                  "Word of Mouth",
                  "Google",
                  "Walk in",
                  "Seminar",
                  "Others",
                ].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      name="reference_source"
                      value={option}
                      onChange={handleReferenceChange}
                      checked={formData.reference_source === option}
                      className="mr-2"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {getErrorMessage("reference_source")}
            </div>
          </div>

          {/* Conditional rendering based on selection */}
          {formData.reference_source === "Reference of Friend/Relative" && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <label
                htmlFor="reference_name_id"
                className="font-semibold mb-1 md:mb-0 sm:w-1/2"
              >
                Please mention referral student name *
              </label>
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  id="reference_name_id"
                  name="reference_name_id"
                  value={formData.reference_name_id || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`border rounded-lg p-2 w-full ${
                    touched.reference_name_id && errors.reference_name_id
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  (Useful for them to claim the referral amount)
                </p>
                {getErrorMessage("reference_name_id")}
              </div>
            </div>
          )}

          {formData.reference_source === "Others" && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <label
                htmlFor="other_reference_source"
                className="font-semibold mb-1 md:mb-0 sm:w-1/2"
              >
                If Others, Mention source of reference *
              </label>
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  id="other_reference_source"
                  name="other_reference_source"
                  value={formData.other_reference_source || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`border rounded-lg p-2 w-full ${
                    touched.other_reference_source &&
                    errors.other_reference_source
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {getErrorMessage("other_reference_source")}
              </div>
            </div>
          )}

          {/* What Test prep is required */}
          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="name_test_preparation_required"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Name of tests you require coaching for *{" "}
              <span className="text-sm font-normal">(Multiple select)</span>
            </label>
            <div className="w-full md:w-1/2">
              <div
                className={`grid grid-cols-1 gap-2 border rounded-lg p-2 bg-white ${
                  touched.name_test_preparation_required &&
                  errors.name_test_preparation_required
                    ? "border-red-500"
                    : ""
                }`}
              >
                {[
                  "GRE",
                  "GMAT",
                  "IELTS/TOEFL/Duolingo/PTE",
                  "PSAT/SAT/ACT",
                  "Indian University Undergraduate Entrance exams (IPMAT, NPAT, FEAT, AAT, CUET)",
                  "LNAT/LSAT",
                  "UCAT/BMAT",
                  "Indian University MBA Entrance Exam (CAT, CET, XAT, IIFT, SNAP, NPAT)",
                  "Indian Universities Law Entrance Exam (CLAT/AILET/MHCET)",
                  "NCHMCT-JEE (Hotel management entrance test)",
                  "Others",
                ].map((option) => (
                  <label className="flex items-center" key={option}>
                    <input
                      type="checkbox"
                      name="name_test_preparation_required"
                      value={option}
                      onChange={handleChange}
                      checked={
                        formData.name_test_preparation_required &&
                        formData.name_test_preparation_required.includes(option)
                      }
                      className="mr-2"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {getErrorMessage("name_test_preparation_required")}
            </div>
          </div>

          {/* Conditionally render the input field for "Others" */}
          {formData.name_test_preparation_required &&
            formData.name_test_preparation_required.includes("Others") && (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <label
                  htmlFor="other_exams"
                  className="font-semibold mb-1 md:mb-0 sm:w-1/2"
                >
                  Please mention other exams you need coaching for *
                </label>
                <div className="w-full md:w-1/2">
                  <input
                    type="text"
                    id="other_exams"
                    name="other_exams"
                    value={formData.other_exams || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`border rounded-lg p-2 w-full ${
                      touched.other_exams && errors.other_exams
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {getErrorMessage("other_exams")}
                </div>
              </div>
            )}

          {/* When do you wish to give the exam */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <label
              htmlFor="exam_date"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              When do you wish to give the exam *
            </label>
            <div className="w-full md:w-1/2">
              <select
                id="exam_date"
                name="exam_date"
                value={formData.exam_date || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`border rounded-lg p-2 w-full ${
                  touched.exam_date && errors.exam_date
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select one from the options below</option>
                <option value="Within 3 months">Within 3 months</option>
                <option value="After 6 months">After 6 months</option>
                <option value="After a year">After a year</option>
              </select>
              {getErrorMessage("exam_date")}
            </div>
          </div>

          {/* Preferred mode of teaching */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <label
              htmlFor="preferred_mode_of_teaching"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Preferred mode of coaching *
            </label>
            <div className="w-full md:w-1/2">
              <select
                id="preferred_mode_of_teaching"
                name="preferred_mode_of_teaching"
                value={formData.preferred_mode_of_teaching || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`border rounded-lg p-2 w-full ${
                  touched.preferred_mode_of_teaching &&
                  errors.preferred_mode_of_teaching
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select one from the options below</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
                <option value="Private">Private</option>
                <option value="Anything works">Anything works</option>
              </select>
              {getErrorMessage("preferred_mode_of_teaching")}
            </div>
          </div>

          {/* Intended Countries */}
          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="country"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Intended Countries *{" "}
              <span className="text-sm font-normal">(Multiple select)</span>
            </label>
            <div className="w-full md:w-1/2">
              <div
                className={`grid grid-cols-2 gap-2  border rounded-lg p-2 bg-white ${
                  touched.name_test_preparation_required &&
                  errors.name_test_preparation_required
                    ? "border-red-500"
                    : ""
                }`}
              >
                {[
                  "USA",
                  "UK",
                  "Australia",
                  "Canada",
                  "Germany",
                  "India",
                  "Ireland",
                  "Newzealand",
                  "Others",
                ].map((option) => (
                  <label className="flex items-center" key={option}>
                    <input
                      type="checkbox"
                      name="country"
                      value={option}
                      onChange={handleChangeCountry}
                      checked={
                        formData.country && formData.country.includes(option)
                      }
                      className="mr-2"
                    />
                    {option === "Newzealand" ? "New Zealand" : option}
                  </label>
                ))}
              </div>
              {getErrorMessage("country")}
            </div>
          </div>

          {/* Conditionally render the input field if "Others" is checked */}
          {formData.country && formData.country.includes("Others") && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <label
                htmlFor="other_country"
                className="font-semibold mb-1 md:mb-0 sm:w-1/2"
              >
                Other Countries For Abroad studies *
              </label>
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  id="other_country"
                  name="other_country"
                  value={formData.other_country || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`border rounded-lg p-2 w-full ${
                    touched.other_country && errors.other_country
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {getErrorMessage("other_country")}
              </div>
            </div>
          )}

          {/* Any special requests */}
          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="special_requests_info"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              Mention any other information/preference if any
            </label>
            <div className="w-full md:w-1/2">
              <textarea
                id="special_requests_info"
                name="special_requests_info"
                value={formData.special_requests_info || ""}
                onChange={handleChange}
                className="border rounded-lg p-2 w-full h-32 border-gray-300"
                placeholder="Please limit your response to 500 words."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center mt-6">
            <button
              type="submit"
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-6 rounded-lg transition duration-300"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TestPrepStudentForm;
